import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  Text,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type CollisionDetection,
} from '@dnd-kit/core';

import SectionCard from '../components/SectionCard';
import {
  DraggableDriverCard,
  DroppableZone,
  VehicleAssignmentColumn,
  type AssignedDriverDragData,
} from '../components/assignment';
import { useMarketplaceToast, useQuoteLock } from '../hooks';
import {
  isLearnerDriver,
  getLearnerLabel,
  hasLicenseData,
} from '../utils/driverAssignment';
import type {
  AssignmentData,
  AssignmentVehicle,
  AssignmentDriver,
  Vehicle,
  Driver,
} from 'api/quote-marketplace';

interface AssignmentStepProps {
  data: AssignmentData;
  fullVehicles: Vehicle[];
  fullDrivers?: Driver[];
  onUpdate: (vehicles: Vehicle[]) => void;
  onDriversUpdate?: (drivers: Driver[]) => void;
  isUpdating?: boolean;
}

const getQuoteProvince = (data: AssignmentData): number => data.province || 1;

const PRIORITY_ZONES = ['active-zone', 'excluded-zone', 'unassign-zone'];

const customCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);

  const priorityCollision = pointerCollisions.find((collision) =>
    PRIORITY_ZONES.includes(String(collision.id)),
  );

  if (priorityCollision) {
    return [priorityCollision];
  }

  return rectIntersection(args);
};

const AssignmentStep = ({
  data,
  fullVehicles,
  fullDrivers,
  onUpdate,
  onDriversUpdate,
}: AssignmentStepProps) => {
  const quoteProvince = getQuoteProvince(data);
  const toast = useMarketplaceToast();
  const { isLocked } = useQuoteLock();
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const panelBg = useColorModeValue('gray.50', 'navy.900');
  const noSensors = useSensors();

  const [vehicles, setVehicles] = useState<AssignmentVehicle[]>(
    data.vehicles || [],
  );
  const [drivers, setDrivers] = useState<AssignmentDriver[]>(
    data.drivers || [],
  );
  const [activeDriver, setActiveDriver] = useState<AssignmentDriver | null>(
    null,
  );
  const [activeAssignedDriver, setActiveAssignedDriver] =
    useState<AssignedDriverDragData | null>(null);

  useEffect(() => {
    if (data.vehicles) {
      setVehicles(data.vehicles);
    }
  }, [data.vehicles]);

  useEffect(() => {
    if (data.drivers) {
      setDrivers(data.drivers);
    }
  }, [data.drivers]);

  const syncToFullVehicles = useCallback(
    (updatedAssignmentVehicles: AssignmentVehicle[]) => {
      const updatedFull = fullVehicles.map((fv) => {
        const av = updatedAssignmentVehicles.find((v) => v.id === fv.id);
        if (av) {
          return {
            ...fv,
            principal_driver_id: av.principal_driver_id,
            secondary_drivers: av.secondary_drivers,
            occasional_drivers: av.occasional_drivers,
            learner_drivers: av.learner_drivers,
            telematics_enabled: av.telematics_enabled,
          };
        }
        return fv;
      });
      onUpdate(updatedFull);
    },
    [fullVehicles, onUpdate],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const driverData = active.data.current?.driver as AssignmentDriver;
    const assignedDriverData = active.data.current
      ?.assignedDriver as AssignedDriverDragData;

    if (driverData) {
      setActiveDriver(driverData);
      setActiveAssignedDriver(null);
    } else if (assignedDriverData) {
      setActiveAssignedDriver(assignedDriverData);
      setActiveDriver(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDriver(null);
    setActiveAssignedDriver(null);

    if (!over) return;

    const dropZoneId = String(over.id);
    const driverData = active.data.current?.driver as AssignmentDriver;
    const isFromExcluded = active.data.current?.isExcluded === true;

    // Handle exclusion zone drops (from active drivers)
    if (driverData && dropZoneId === 'excluded-zone' && !isFromExcluded) {
      handleExcludeDriver(driverData.id);
      return;
    }

    // Handle active zone drops (from excluded drivers)
    if (driverData && dropZoneId === 'active-zone' && isFromExcluded) {
      handleRemoveExcluded(driverData.id);
      return;
    }

    const assignedDriverData = active.data.current
      ?.assignedDriver as AssignedDriverDragData;

    if (assignedDriverData) {
      const dropZoneId = over?.id ? String(over.id) : null;
      const driverId = assignedDriverData.driverId;
      const sourceVehicleId = assignedDriverData.sourceVehicleId;
      const sourceZone = assignedDriverData.sourceZone;

      const isUnassignZone = dropZoneId === 'unassign-zone';

      if (!dropZoneId || isUnassignZone) {
        handleRemoveDriver(sourceVehicleId, driverId, sourceZone);
        return;
      }

      const [targetZone, targetVehicleIdStr] = dropZoneId.split('-');
      const targetVehicleId = parseInt(targetVehicleIdStr, 10);

      const isVehicleAssignmentZone =
        !isNaN(targetVehicleId) &&
        (targetZone === 'principal' ||
          targetZone === 'secondary' ||
          targetZone === 'occasional' ||
          targetZone === 'learners');

      if (!isVehicleAssignmentZone) {
        handleRemoveDriver(sourceVehicleId, driverId, sourceZone);
        return;
      }

      const isSamePosition =
        targetVehicleId === sourceVehicleId && targetZone === sourceZone;

      if (isSamePosition) {
        return;
      }

      let updated = [...vehicles];

      const sourceIndex = updated.findIndex((v) => v.id === sourceVehicleId);
      if (sourceIndex !== -1) {
        const sourceVehicle = updated[sourceIndex];
        if (sourceZone === 'principal') {
          updated[sourceIndex] = {
            ...sourceVehicle,
            principal_driver_id: null,
          };
        } else if (sourceZone === 'secondary') {
          updated[sourceIndex] = {
            ...sourceVehicle,
            secondary_drivers: (sourceVehicle.secondary_drivers ?? []).filter(
              (id) => id !== driverId,
            ),
          };
        } else if (sourceZone === 'occasional') {
          updated[sourceIndex] = {
            ...sourceVehicle,
            occasional_drivers: (sourceVehicle.occasional_drivers ?? []).filter(
              (id) => id !== driverId,
            ),
          };
        } else if (sourceZone === 'learners') {
          updated[sourceIndex] = {
            ...sourceVehicle,
            learner_drivers: (sourceVehicle.learner_drivers ?? []).filter(
              (id) => id !== driverId,
            ),
          };
        }
      }

      const targetIndex = updated.findIndex((v) => v.id === targetVehicleId);
      if (targetIndex !== -1) {
        const targetVehicle = updated[targetIndex];

        if (targetZone === 'principal') {
          if (!canAssignAsPrincipal(driverId, targetVehicleId)) {
            toast.warning(
              'Each licensed driver must be principal on at least one vehicle before assigning the same driver to multiple vehicles',
            );
            return;
          }

          const targetSecondary = targetVehicle.secondary_drivers ?? [];
          const targetOccasional = targetVehicle.occasional_drivers ?? [];
          updated[targetIndex] = {
            ...targetVehicle,
            principal_driver_id: driverId,
            secondary_drivers: targetSecondary.filter((id) => id !== driverId),
            occasional_drivers: targetOccasional.filter(
              (id) => id !== driverId,
            ),
          };
        } else if (targetZone === 'secondary') {
          const targetSecondary = targetVehicle.secondary_drivers ?? [];
          const targetOccasional = targetVehicle.occasional_drivers ?? [];
          if (!targetSecondary.includes(driverId)) {
            updated[targetIndex] = {
              ...targetVehicle,
              secondary_drivers: [...targetSecondary, driverId],
              occasional_drivers: targetOccasional.filter(
                (id) => id !== driverId,
              ),
            };
          }
        } else if (targetZone === 'occasional') {
          const targetSecondary = targetVehicle.secondary_drivers ?? [];
          const targetOccasional = targetVehicle.occasional_drivers ?? [];
          if (!targetOccasional.includes(driverId)) {
            updated[targetIndex] = {
              ...targetVehicle,
              secondary_drivers: targetSecondary.filter(
                (id) => id !== driverId,
              ),
              occasional_drivers: [...targetOccasional, driverId],
            };
          }
        } else if (targetZone === 'learners') {
          const currentLearners = targetVehicle.learner_drivers ?? [];
          if (!currentLearners.includes(driverId)) {
            updated[targetIndex] = {
              ...targetVehicle,
              learner_drivers: [...currentLearners, driverId],
            };
          }
        }
      }

      setVehicles(updated);
      syncToFullVehicles(updated);
      return;
    }

    // Handle pool driver drops on vehicle zones
    if (!driverData) return;

    const [zone, vehicleIdStr] = dropZoneId.split('-');
    const vehicleId = parseInt(vehicleIdStr, 10);

    if (isNaN(vehicleId)) return;

    if (isFromExcluded) {
      toast.warning('Excluded drivers cannot be assigned to vehicles');
      return;
    }

    if (!hasLicenseData(driverData, quoteProvince)) {
      toast.warning(
        'Driver cannot be assigned without license information. Please add license details first.',
      );
      return;
    }

    const driverIsLearner = isLearnerDriver(driverData, quoteProvince);

    if (zone === 'learners') {
      if (!driverIsLearner) {
        toast.warning('This driver does not qualify as a Learner');
        return;
      }

      const updated = [...vehicles];
      const vehicleIndex = updated.findIndex((v) => v.id === vehicleId);
      if (vehicleIndex === -1) return;

      const vehicle = updated[vehicleIndex];
      const currentLearners = vehicle.learner_drivers ?? [];

      if (currentLearners.includes(driverData.id)) {
        return;
      }

      updated[vehicleIndex] = {
        ...vehicle,
        learner_drivers: [...currentLearners, driverData.id],
      };

      setVehicles(updated);
      syncToFullVehicles(updated);
      return;
    }

    if (driverIsLearner) {
      const learnerLabel = getLearnerLabel(quoteProvince);
      toast.warning(`${learnerLabel} drivers can only be assigned as learners`);
      return;
    }

    const updated = [...vehicles];
    const vehicleIndex = updated.findIndex((v) => v.id === vehicleId);
    if (vehicleIndex === -1) return;

    const vehicle = updated[vehicleIndex];
    const secondaryDrivers = vehicle.secondary_drivers ?? [];
    const occasionalDrivers = vehicle.occasional_drivers ?? [];

    if (zone === 'principal') {
      if (!canAssignAsPrincipal(driverData.id, vehicleId)) {
        toast.warning(
          'Each licensed driver must be principal on at least one vehicle before assigning the same driver to multiple vehicles',
        );
        return;
      }

      updated[vehicleIndex] = {
        ...vehicle,
        principal_driver_id: driverData.id,
        secondary_drivers: secondaryDrivers.filter(
          (id) => id !== driverData.id,
        ),
        occasional_drivers: occasionalDrivers.filter(
          (id) => id !== driverData.id,
        ),
      };

      setVehicles(updated);
      syncToFullVehicles(updated);
    } else if (zone === 'secondary') {
      if (vehicle.principal_driver_id === driverData.id) {
        toast.info('Driver is already the principal operator');
        return;
      }

      if (secondaryDrivers.includes(driverData.id)) {
        return;
      }

      updated[vehicleIndex] = {
        ...vehicle,
        secondary_drivers: [...secondaryDrivers, driverData.id],
        occasional_drivers: occasionalDrivers.filter(
          (id) => id !== driverData.id,
        ),
      };

      setVehicles(updated);
      syncToFullVehicles(updated);
    } else if (zone === 'occasional') {
      if (vehicle.principal_driver_id === driverData.id) {
        toast.info('Driver is already the principal operator');
        return;
      }

      if (occasionalDrivers.includes(driverData.id)) {
        return;
      }

      updated[vehicleIndex] = {
        ...vehicle,
        secondary_drivers: secondaryDrivers.filter(
          (id) => id !== driverData.id,
        ),
        occasional_drivers: [...occasionalDrivers, driverData.id],
      };

      setVehicles(updated);
      syncToFullVehicles(updated);
    }
  };

  const handleRemoveDriver = (
    vehicleId: number,
    driverId: number,
    zone: 'principal' | 'secondary' | 'occasional' | 'learners',
  ) => {
    const updated = [...vehicles];
    const vehicleIndex = updated.findIndex((v) => v.id === vehicleId);
    if (vehicleIndex === -1) return;

    const vehicle = updated[vehicleIndex];

    if (zone === 'principal') {
      updated[vehicleIndex] = {
        ...vehicle,
        principal_driver_id: null,
      };
    } else if (zone === 'secondary') {
      updated[vehicleIndex] = {
        ...vehicle,
        secondary_drivers: (vehicle.secondary_drivers ?? []).filter(
          (id) => id !== driverId,
        ),
      };
    } else if (zone === 'occasional') {
      updated[vehicleIndex] = {
        ...vehicle,
        occasional_drivers: (vehicle.occasional_drivers ?? []).filter(
          (id) => id !== driverId,
        ),
      };
    } else if (zone === 'learners') {
      updated[vehicleIndex] = {
        ...vehicle,
        learner_drivers: (vehicle.learner_drivers ?? []).filter(
          (id) => id !== driverId,
        ),
      };
    }

    setVehicles(updated);
    syncToFullVehicles(updated);
  };

  const handleTelematicsChange = (vehicleId: number, enabled: boolean) => {
    const updated = vehicles.map((v) =>
      v.id === vehicleId ? { ...v, telematics_enabled: enabled } : v,
    );
    setVehicles(updated);
    syncToFullVehicles(updated);
  };

  const assignedDriverIds = new Set<number>();
  for (const vehicle of vehicles) {
    if (vehicle.principal_driver_id !== null) {
      assignedDriverIds.add(vehicle.principal_driver_id);
    }
    for (const id of vehicle.secondary_drivers ?? []) {
      assignedDriverIds.add(id);
    }
    for (const id of vehicle.occasional_drivers ?? []) {
      assignedDriverIds.add(id);
    }
  }

  const licensedDrivers = drivers.filter(
    (d) => !d.excluded && !isLearnerDriver(d, quoteProvince),
  );

  const learnerDrivers = drivers.filter(
    (d) => !d.excluded && isLearnerDriver(d, quoteProvince),
  );

  const excludedDrivers = drivers.filter((d) => d.excluded);

  const handleRemoveExcluded = useCallback(
    (driverId: number) => {
      const updatedLocalDrivers = drivers.map((d) =>
        d.id === driverId ? { ...d, excluded: false } : d,
      );
      setDrivers(updatedLocalDrivers);

      if (fullDrivers && onDriversUpdate) {
        const updatedFullDrivers = fullDrivers.map((d) =>
          d.id === driverId ? { ...d, excluded: false } : d,
        );
        onDriversUpdate(updatedFullDrivers);
      }
    },
    [drivers, fullDrivers, onDriversUpdate],
  );

  const handleExcludeDriver = useCallback(
    (driverId: number) => {
      const updatedLocalDrivers = drivers.map((d) =>
        d.id === driverId ? { ...d, excluded: true } : d,
      );
      setDrivers(updatedLocalDrivers);

      // Remove from all vehicle assignments
      const updatedVehicles = vehicles.map((v) => ({
        ...v,
        principal_driver_id:
          v.principal_driver_id === driverId ? null : v.principal_driver_id,
        secondary_drivers: (v.secondary_drivers ?? []).filter(
          (id) => id !== driverId,
        ),
        occasional_drivers: (v.occasional_drivers ?? []).filter(
          (id) => id !== driverId,
        ),
        learner_drivers: (v.learner_drivers ?? []).filter(
          (id) => id !== driverId,
        ),
      }));
      setVehicles(updatedVehicles);
      syncToFullVehicles(updatedVehicles);

      if (fullDrivers && onDriversUpdate) {
        const updatedFullDrivers = fullDrivers.map((d) =>
          d.id === driverId ? { ...d, excluded: true } : d,
        );
        onDriversUpdate(updatedFullDrivers);
      }
    },
    [drivers, vehicles, fullDrivers, onDriversUpdate, syncToFullVehicles],
  );

  const canAssignAsPrincipal = useCallback(
    (driverId: number, targetVehicleId: number): boolean => {
      if (licensedDrivers.length <= 1) return true;
      if (vehicles.length < licensedDrivers.length) return true;

      const isAlreadyPrincipalElsewhere = vehicles.some(
        (v) => v.principal_driver_id === driverId && v.id !== targetVehicleId,
      );

      if (!isAlreadyPrincipalElsewhere) return true;

      const principalDriverIds = new Set(
        vehicles
          .map((v) => v.principal_driver_id)
          .filter((id): id is number => id !== null),
      );

      const hasDriverWithoutPrincipal = licensedDrivers.some(
        (d) => !principalDriverIds.has(d.id),
      );

      if (hasDriverWithoutPrincipal) {
        return false;
      }

      return true;
    },
    [licensedDrivers, vehicles],
  );

  return (
    <DndContext
      collisionDetection={customCollisionDetection}
      sensors={isLocked ? noSensors : undefined}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Flex gap={4} h="100%" overflow="hidden">
        <Box
          w="220px"
          flexShrink={0}
          bg={panelBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={3}
          overflowY="auto"
        >
          {/* Unassign zone - separate from driver zones */}
          {activeAssignedDriver && (
            <DroppableZone id="unassign-zone">
              {(isOver) => (
                <Box
                  p={2}
                  mb={3}
                  borderRadius="md"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={isOver ? 'red.400' : 'red.200'}
                  bg={isOver ? 'red.50' : 'transparent'}
                  transition="all 0.15s"
                >
                  <Text
                    fontSize="xs"
                    color={isOver ? 'red.600' : 'red.400'}
                    textAlign="center"
                    fontWeight="500"
                  >
                    {isOver ? 'Release to unassign' : 'Drop here to unassign'}
                  </Text>
                </Box>
              )}
            </DroppableZone>
          )}

          {/* Active drivers zone */}
          <DroppableZone id="active-zone">
            {(isOver) => (
              <Box
                p={3}
                minH="80px"
                borderRadius="md"
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={
                  isOver
                    ? 'blue.400'
                    : activeDriver?.excluded
                      ? 'blue.300'
                      : 'gray.200'
                }
                bg={isOver ? 'blue.50' : 'transparent'}
                transition="all 0.15s"
              >
                {(isOver || activeDriver?.excluded) && (
                  <Text
                    fontSize="xs"
                    color={isOver ? 'blue.600' : 'blue.400'}
                    textAlign="center"
                    mb={2}
                    fontWeight="500"
                  >
                    {isOver
                      ? 'Release to include driver'
                      : 'Drop here to include'}
                  </Text>
                )}
                <VStack
                  spacing={2}
                  align="stretch"
                  minH="40px"
                  justify="center"
                >
                  {licensedDrivers.map((driver) => (
                    <DraggableDriverCard key={driver.id} driver={driver} />
                  ))}
                  {learnerDrivers.map((driver) => (
                    <DraggableDriverCard
                      key={driver.id}
                      driver={driver}
                      isLearner
                      learnerLabel={driver.license_class}
                    />
                  ))}
                  {licensedDrivers.length === 0 &&
                    learnerDrivers.length === 0 && (
                      <Text
                        fontSize="xs"
                        color="gray.400"
                        textAlign="center"
                        py={2}
                      >
                        No active drivers
                      </Text>
                    )}
                </VStack>
              </Box>
            )}
          </DroppableZone>

          <Divider my={3} />

          {/* Excluded drivers zone */}
          <DroppableZone id="excluded-zone">
            {(isOver) => (
              <Box
                p={3}
                minH="80px"
                borderRadius="md"
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={
                  isOver
                    ? 'red.400'
                    : activeDriver && !activeDriver.excluded
                      ? 'red.300'
                      : 'gray.200'
                }
                bg={isOver ? 'red.50' : 'transparent'}
                transition="all 0.15s"
              >
                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>
                  {isOver ? 'Drop to exclude driver' : 'Excluded'}
                </Text>
                <VStack
                  spacing={2}
                  align="stretch"
                  minH="40px"
                  justify="center"
                >
                  {excludedDrivers.map((driver) => (
                    <DraggableDriverCard
                      key={driver.id}
                      driver={driver}
                      isExcluded
                      onRemove={() => handleRemoveExcluded(driver.id)}
                    />
                  ))}
                  {excludedDrivers.length === 0 && (
                    <Text
                      fontSize="xs"
                      color="gray.400"
                      textAlign="center"
                      py={2}
                    >
                      No excluded drivers
                    </Text>
                  )}
                </VStack>
              </Box>
            )}
          </DroppableZone>
        </Box>

        <Box flex={1} overflowX="auto">
          <SectionCard title="Vehicle Assignments" h="100%">
            <Flex gap={4} pb={2} overflowX="auto">
              {vehicles.map((vehicle) => (
                <VehicleAssignmentColumn
                  key={vehicle.id}
                  vehicle={vehicle}
                  drivers={drivers}
                  quoteProvince={quoteProvince}
                  onRemoveDriver={handleRemoveDriver}
                  onTelematicsChange={handleTelematicsChange}
                />
              ))}
              {vehicles.length === 0 && (
                <Text color="gray.500">No vehicles to assign</Text>
              )}
            </Flex>
          </SectionCard>
        </Box>
      </Flex>

      <DragOverlay>
        {activeDriver ? (
          <DraggableDriverCard
            driver={activeDriver}
            isDragOverlay
            isExcluded={activeDriver.excluded}
          />
        ) : activeAssignedDriver ? (
          <Box
            bg="blue.50"
            borderWidth="1px"
            borderColor="blue.200"
            borderRadius="md"
            px={3}
            py={2}
            shadow="lg"
          >
            <Text fontSize="sm" fontWeight="500">
              {activeAssignedDriver.driverName}
            </Text>
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default AssignmentStep;
